import { FeatureFlagKey } from '@/types';
import Iconify from '@/components/media/iconify';
import { ShowIf } from '@/utils/ui/showIf';
import { useBoolean } from '@/hooks/state/use-boolean';
import { useFeatureFlag } from '@/hooks/data/use-feature-flag';
import { useOdooUnlink } from '@/hooks/integrations/use-odoo-unlink';
import { OdooResourceType } from '@/types/odoo.types';
import ConfirmDialog from '@/components/overlays/custom-dialog/confirm-dialog';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import CardHeader from '@mui/material/CardHeader';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';

import { useTranslation } from 'react-i18next';

import { OdooStatusBadge } from './odoo-status-badge';
import { OdooLinkDialog } from './odoo-link-dialog';
import { OdooPushDialog } from './odoo-push-dialog';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface OdooIntegrationSectionProps {
  /** The Odoo entity type. */
  resourceType: OdooResourceType;
  /** The Hub resource MongoDB ID. */
  resourceId: string;
  /** Display name of the Hub resource. */
  resourceName: string;
  /** The Odoo external ID if already linked. */
  externalId?: number | null;
  /** Summary data of the Hub resource (used for push preview). */
  resourceData?: Record<string, unknown>;
  /** Called after any link/unlink/push operation succeeds. */
  onUpdate?: () => void | Promise<unknown>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const odooFlagByResource: Record<OdooResourceType, FeatureFlagKey> = {
  [OdooResourceType.ORGANIZATIONS]: FeatureFlagKey.ENABLE_ODOO_ORGANIZATIONS_SYNC,
  [OdooResourceType.LEGAL_ENTITIES]: FeatureFlagKey.ENABLE_ODOO_LEGAL_ENTITIES_SYNC,
  [OdooResourceType.CONTACTS]: FeatureFlagKey.ENABLE_ODOO_CONTACT_SYNC,
  [OdooResourceType.PLANS]: FeatureFlagKey.ENABLE_ODOO_PRODUCTS_SYNC,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OdooIntegrationSection({
  resourceType,
  resourceId,
  resourceName,
  externalId,
  resourceData,
  onUpdate,
}: OdooIntegrationSectionProps) {
  const { t } = useTranslation();
  const { enabled: isOdooEnabled, isLoading: isFlagLoading } = useFeatureFlag(
    odooFlagByResource[resourceType]
  );

  // Dialog states
  const linkDialog = useBoolean();
  const pushDialog = useBoolean();
  const unlinkConfirm = useBoolean();

  // Unlink mutation
  const { unlink, isLoading: isUnlinking } = useOdooUnlink({
    resourceType,
    onSuccess: () => {
      unlinkConfirm.onFalse();
      onUpdate?.();
    },
  });

  const isLinked = externalId != null;

  // Don't render while loading the feature flag
  if (isFlagLoading) return null;

  // Don't render if the feature flag is not enabled
  if (!isOdooEnabled) return null;

  return (
    <>
      <Card variant="outlined">
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              <Iconify icon="mdi:swap-horizontal" width={22} />
              <Typography variant="subtitle1">{t('Odoo Integration')}</Typography>
            </Stack>
          }
          action={<OdooStatusBadge externalId={externalId} />}
        />

        <Divider />

        <CardContent>
          <ShowIf
            condition={isLinked}
            alternative={
              <Typography variant="body2" color="text.secondary">
                {t(
                  'This resource is not linked to any Odoo record. You can link it to an existing Odoo record or create a new one in Odoo.'
                )}
              </Typography>
            }
          >
            <Typography variant="body2" color="text.secondary">
              {t('This resource is linked to Odoo record ID')}: <strong>{externalId}</strong>
            </Typography>
          </ShowIf>
        </CardContent>

        <Divider />

        <CardActions sx={{ px: 2, py: 1.5 }}>
          <ShowIf
            condition={isLinked}
            alternative={
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Iconify icon="mdi:link" width={18} />}
                  onClick={linkDialog.onTrue}
                >
                  {t('Link with Odoo')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Iconify icon="mdi:cloud-upload" width={18} />}
                  onClick={pushDialog.onTrue}
                >
                  {t('Create in Odoo')}
                </Button>
              </Stack>
            }
          >
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<Iconify icon="mdi:link-off" width={18} />}
              onClick={unlinkConfirm.onTrue}
              disabled={isUnlinking}
            >
              {isUnlinking ? t('Unlinking...') : t('Unlink')}
            </Button>
          </ShowIf>
        </CardActions>
      </Card>

      {/* Link Dialog */}
      <OdooLinkDialog
        open={linkDialog.value}
        onClose={linkDialog.onFalse}
        resourceType={resourceType}
        resourceId={resourceId}
        resourceName={resourceName}
        onSuccess={() => onUpdate?.()}
      />

      {/* Push Dialog */}
      <OdooPushDialog
        open={pushDialog.value}
        onClose={pushDialog.onFalse}
        resourceType={resourceType}
        resourceId={resourceId}
        resourceName={resourceName}
        resourceData={resourceData}
        onSuccess={() => onUpdate?.()}
      />

      {/* Unlink Confirmation Dialog */}
      <ConfirmDialog
        open={unlinkConfirm.value}
        onClose={unlinkConfirm.onFalse}
        title={t('Unlink from Odoo')}
        content={t(
          'Are you sure you want to remove the link between this resource and the Odoo record? This will not delete the Odoo record.'
        )}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => unlink(resourceId)}
            disabled={isUnlinking}
          >
            {isUnlinking ? t('Unlinking...') : t('Unlink')}
          </Button>
        }
      />
    </>
  );
}
